import React, { useState } from 'react';
import { TextField, Box, SxProps, Typography } from '@mui/material';
import { Button } from 'system/atoms';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useToast, ToastType } from 'services/toast';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { useAuth } from 'services/auth/hooks';
import { PasswordInput } from 'components/Inputs';
import { RouterLink } from 'common/ui/RouterLink';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  justifyContent: 'center',
  padding: 4,
  paddingTop: '10%'
};

const formStyle: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: 400,
  mb: 12
};

const fieldStyle = {
  width: '100%',
  mb: 2
};

const buttonStyle = {
  width: '100%',
  mt: 2
};

const verificationCodeSchema = Yup.object({
  verificationCode: Yup.string().required('Verification code is required'),
  newPassword: Yup.string().required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm your password')
});

const emailSendingSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Add email address. Must include an @ symbol.')
});

export const ResetPasswordPage: React.FC = () => {
  const { showToast } = useToast();
  const { forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [isVerificationStep, setIsVerificationStep] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      verificationCode: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: isVerificationStep
      ? verificationCodeSchema
      : emailSendingSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (isVerificationStep) {
          await resetPassword(
            values.email,
            values.verificationCode,
            values.newPassword
          );
          showToast({
            message: 'Password reset successful.',
            type: ToastType.Success
          });
          navigate(Paths.LOGIN);
        } else {
          await forgotPassword(values.email);
          showToast({
            message: 'Verification code sent. Check your email.',
            type: ToastType.Success
          });
          setIsVerificationStep(true);
        }
      } catch (error: any) {
        showToast({
          message: 'We could not reset your password',
          type: ToastType.Error
        });
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <Box sx={container}>
      <Box sx={{ width: 500 }}>
        <Typography
          sx={{ color: 'primary.main', fontWeight: 'bold', marginBottom: 3 }}
          variant="h5"
        >
          Reset Your Password
        </Typography>
        <Box onSubmit={formik.handleSubmit} sx={formStyle} component="form">
          {isVerificationStep ? (
            <>
              <TextField
                name="verificationCode"
                autoComplete="verification-code"
                placeholder="Verification Code"
                onChange={formik.handleChange}
                value={formik.values.verificationCode}
                fullWidth
                InputProps={{
                  sx: fieldStyle
                }}
              />
              <PasswordInput
                name="newPassword"
                autoComplete="new-password"
                type="password"
                placeholder="New Password"
                onChange={formik.handleChange}
                value={formik.values.newPassword}
                fullWidth
                InputProps={{
                  sx: fieldStyle
                }}
              />
              <PasswordInput
                name="confirmPassword"
                autoComplete="new-password"
                type="password"
                placeholder="Confirm New Password"
                onChange={formik.handleChange}
                value={formik.values.confirmPassword}
                fullWidth
                InputProps={{
                  sx: fieldStyle
                }}
              />
            </>
          ) : (
            <TextField
              name="email"
              type="email"
              placeholder="Email"
              onChange={formik.handleChange}
              value={formik.values.email}
              fullWidth
              InputProps={{
                sx: fieldStyle
              }}
            />
          )}
          <Button
            variant="primary"
            type="submit"
            sx={buttonStyle}
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {formik.isSubmitting
              ? 'Processing...'
              : isVerificationStep
              ? 'Reset password'
              : 'Send reset code'}
          </Button>
          <Box height={16} />
          <RouterLink to={Paths.LOGIN}>
            <Button variant="tertiary">Back to Login</Button>
          </RouterLink>
        </Box>
      </Box>
    </Box>
  );
};
